import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Button, Card, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowAltCircleLeft, faBell, faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faEdit, faFilePen, faRedo, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams, useLocation, matchPath, } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../template/store';
import useDataCountry from '../../../hooks/useDataCountry';
import { useBackNavigation } from '../../../hooks/BackNavigation';
import { updateDashbaordRefresh } from '../../common/components/DataTable/slices/DataTable';
import DataTable from '../../common/components/DataTable';
import { getAllInvoicesForBl } from '../dashboard/actions/instruction';
import { getInstructionList } from '../dashboard/actions/instruction';
import {
    submitRefundRequest,
    getRefundTypeList,
    getRefundCountryAreaCodeList,
    getRefundBankList,
    getRefundCcyList,
    getRefundDetails,
    approveRejectRefund,
    getRefundCustomerAttConf,
} from './RefundActions';
import RefundCustomerDocuments from './RefundCustomerDocuments';
import { REFUND_EVENT_STATUS_ID, REFUND_EVENT_STATUS_LABEL } from '../../constants/RefundConstant';
import {
    clearRefundViewAndCustomerState,
    updateCustomerAttData,
    updateSelectedCustomerAttachmentFilePath,
    updateSelectedCustomerAttachmentName,
    updateSelectedCustomerFileServerPath,
    clearCustomerFields,
} from './RefundSlice';
// import RefundLinerDocuments from './RefundLinerDocuments';
import { AESDecrypt } from '../../../encrypt-util';
import useBlCountry from '../../../hooks/useBlCountry';
import { getUnprefixedPathname } from '../../../helpers/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

type FormValues = {
    /* BL Number / Consignee / Requested By — UI + payload disabled; use OBL `oblNumber` for BL
    blNumber: string;
    consignee: string;
    requestedBy: string;
    */
    countryAreaCode: string;
    contactNumber: string;
    refundType: string;
    refundTo: string;
    remarks: string;
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    swiftCode: string;
    statusLabel: string;
    statusId: number | null;
    deductionAmount: string;
    deductionCcy: string;
    refundedAmount: string;
    // refundedCcy: string;
    linerRemarks: string;
    actionType: string;
};


const sanitizeAmountInput = (raw: string) =>
    raw.replace(/[^0-9.,]/g, '');


const validationSchema = Yup.object({
    countryAreaCode: Yup.string().required('Contact Code is required'),

    contactNumber: Yup.string().trim().required('Contact Number is required'),


    refundType: Yup.string().required('Refund Type is required'),

    // refundTo: Yup.string().required('Refund To is required'),

    refundTo: Yup.string().required('Refund To is required'),

    bankName: Yup.string().required('Bank Name is required'),

    remarks: Yup.string().optional(),

    accountHolderName: Yup.string().required('Account Holder Name is required'),

    accountNumber: Yup.string().required('Account Number is required'),

    swiftCode: Yup.string().optional(),

    actionType: Yup.string().optional(),

    // deductionAmount: Yup.string().when('actionType', {
    //     is: 'A',
    //     then: (schema) =>
    //         schema
    //             .required('Deduction amount is required')
    //             .matches(/^[0-9.,]+$/, {
    //                 message: 'Only digits (0-9), commas, and a decimal point (.) are allowed',
    //                 excludeEmptyString: true,
    //             }),
    //     otherwise: (schema) => schema.optional(),
    // }),



    deductionAmount: Yup.string().when('actionType', {
        is: 'A',
        then: (schema) =>
            schema
                .required('Deduction amount is required')
                .matches(/^\d{1,15}(\.\d{1,3})?$/, {
                    message: 'Only numbers allowed with up to 3 decimal places',
                    excludeEmptyString: true,
                }),
        otherwise: (schema) => schema.optional(),
    }),


    deductionCcy: Yup.string().when('actionType', {
        is: 'A',
        then: (schema) => schema.required('Currency is required'),
        otherwise: (schema) => schema.optional(),
    }),

    // refundedAmount: Yup.string().when('actionType', {
    //     is: 'A',
    //     then: (schema) =>
    //         schema
    //             .required('Refund Amount is required')
    //             .matches(/^[0-9.,]+$/, {
    //                 message: 'Only digits (0-9), commas, and a decimal point (.) are allowed',
    //                 excludeEmptyString: true,
    //             }),
    //     otherwise: (schema) => schema.optional(),
    // }),


    refundedAmount: Yup.string().when('actionType', {
        is: 'A',
        then: (schema) =>
            schema
                .required('Refund Amount is required')
                .matches(/^\d{1,15}(\.\d{1,3})?$/, {
                    message: 'Only numbers allowed with up to 3 decimal places',
                    excludeEmptyString: true,
                }),
        otherwise: (schema) => schema.optional(),
    }),

    // refundedCcy: Yup.string().when('actionType', {
    //     is: 'A',
    //     then: (schema) => schema.required('Refund Currency is required'),
    //     otherwise: (schema) => schema.optional(),
    // }),

    linerRemarks: Yup.string().when('actionType', {
        is: (actionType: string) => actionType === 'R',
        then: (schema) =>
            schema
                .required('Liner Remarks is required')
                .max(100, 'Maximum 100 characters'),
        otherwise: (schema) => schema.optional(),
    }),
});

const Refund = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const location = useLocation();
    const dataCountry = useDataCountry();
    const onBack = useBackNavigation();
    const { blNum, encrypt, encryptdoc } = useParams();
    const oblNumber = useSelector((state: RootState) => state.oblDetails.oblForm.number);
    const oblPartyList = useSelector((state: RootState) => state.oblDetails.data.partyList);
    const profileDetails = useSelector((state: RootState) => state.userProfile.profile);
    const rbac = useSelector((state: RootState) => state.userProfile);
    const selectedLinerId = useSelector((state: RootState) => state.navbarTop.linerId);
    //  const releasePOPData = useSelector((state: RootState) => state.invoiceUnpaid.releasePOPData);

    const docAttachment = useSelector((state: RootState) => state.refund.customerAttData);
    const selectedAttachmentName = useSelector((state: RootState) => state.refund.selectedCustomerAttachmentName);
    const selectedAttachmentFilePath = useSelector((state: RootState) => state.refund.selectedCustomerAttachmentFilePath);
    const serverFilePath = useSelector((state: RootState) => state.refund.selectedCustomerFileServerPath);

    const refundViewForm = useSelector(
        (state: RootState) => state.refund.refundFormView
    );
    const instructionList = useSelector((state: RootState) => state.documentHistory.data);
    const loginPartnerId = useSelector((state: RootState) => state.userProfile?.profile?.partnerId);

    const APP_PATH_PREFIX = process.env.REACT_APP_APP_PATH_PREFIX || '';

    const matchPth = [
        {
            path: `${APP_PATH_PREFIX}/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[3] !== 'instruction',
        },
        {
            path: `${APP_PATH_PREFIX}/instruction/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[4] !== 'instruction',
        },
        {
            path: `${APP_PATH_PREFIX}/payment/bl/:blNum/:encrypt/:encryptdoc`,
            condition: getUnprefixedPathname().split('/')[4] !== 'instruction',
        },
        { path: `${APP_PATH_PREFIX}/instruction/:blNum/:encrypt/:encryptdoc` },
    ].some(({ path, condition }: { path: string; condition?: boolean }) => {
        return (
            (condition ?? true) &&
            matchPath({ path, caseSensitive: true, end: true }, location.pathname)
        );
    });

    const [blCountry, setBlCountry] = useBlCountry(
        matchPth ? "view" : "new",
        "import",
        821
    );

    const linerId =
        profileDetails?.partnerType === 'liner'
            ? profileDetails?.partnerId
            : selectedLinerId;

    const [disableFields, setDisableFields] = useState<any>(
        profileDetails?.partnerType === 'liner' ||
        (!!matchPth && profileDetails?.partnerType !== 'liner')
    );

    console.log(disableFields, "disableFields")


    const recentId = useSelector((state: RootState) => {
        const arr = state?.documentHistory?.data?.filter(
            (inst) =>
                inst.instructionId == 821 &&
                (inst.event == 'Submitted' || inst.event == 'Draft' || inst.event == 'Not Submitted') && (inst.partnerId == null || inst.partnerId == loginPartnerId)
        );
        const recentInst = arr.length <= 0 ? null :
            arr.reduce((a: any, b: any) => (a.blEventId > b.blEventId ? a : b))

        return ['Y', '1', '2', '3', '4', '5', '6'].includes(recentInst?.docStatus ?? '')
            ? null
            : recentInst;
    });


    const refundRecentEventAllowsSaveAsDraft =
        recentId == null ||
        recentId?.event === REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED ||
        recentId?.event === 'Draft';

    const [refundTypeList, setRefundTypeList] = useState<any[]>([]);
    const [countryAreaCodeList, setCountryAreaCodeList] = useState<any[]>([]);
    const [bankNameList, setBankNameList] = useState<any[]>([]);
    const [refundCcyList, setRefundCcyList] = useState<any[]>([]);

    const [customerLoadingFileState, setCustomerLoadingFileState] = useState<Record<string, any>>({});
    const [blAllInvoicesRows, setBlAllInvoicesRows] = useState<any[]>([]);



    // const linerDocAttachment = useSelector((state: RootState) => state.refund.linerAttData);
    // const selectedLinerAttachmentName = useSelector((state: RootState) => state.refund.selectedLinerAttachmentName);
    // const selectedLinerAttachmentFilePath = useSelector((state: RootState) => state.refund.selectedLinerAttachmentFilePath);
    // const linerServerFilePath = useSelector((state: RootState) => state.refund.selectedLinerFileServerPath);
    // const [linerLoadingFileState, setLinerLoadingFileState] = useState<Record<string, any>>({});

    const refundInvoiceList = useMemo(
        () => [
            {
                accessor: 'invno',
                Header: 'Invoice Number',
                width: 200,
                maxWidth: 150,
            },
            {
                accessor: 'invtype',
                Header: 'Invoice Type',
                width: 200,
                maxWidth: 150,
            },
            {
                accessor: 'invstatus',
                Header: 'Event',
                width: 100,

                maxWidth: 80,
                Cell: (rowdata: any) => {
                    const { invstatus, invoicestatus } = rowdata.row.original
                    return <span>{invstatus && invstatus?.trim() != '' ? invstatus : invoicestatus == 1 ? "Paid" : ""}</span>
                }
            }
        ],
        []
    );

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        clearErrors,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            // BL Number / Consignee / Requested By — omitted (use OBL `oblNumber` for submit)
            // blNumber: '',
            // consignee: '',
            // requestedBy: '',
            countryAreaCode: '',
            contactNumber: '',
            refundType: '',
            refundTo: '',
            remarks: '',
            accountHolderName: '',
            bankName: '',
            accountNumber: '',
            swiftCode: '',
            statusLabel: '',
            statusId: null,
            deductionAmount: '',
            deductionCcy: '',
            refundedAmount: '',
            // refundedCcy: '',
            linerRemarks: '',
            actionType: '',
        },
    });

    const displayNumber = oblNumber ? String(oblNumber) : '';

    const handleCopyBL = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            if (displayNumber) {
                await navigator.clipboard.writeText(
                    typeof displayNumber === 'string' ? displayNumber : String(displayNumber ?? '')
                );
            } else {
                toast.error(t('resource_2:copyFailed') as string);
            }
        } catch (error) {
            console.error('Copy failed:', error);
            toast.error(t('resource_2:copyFailed') as string);
        }
    };

    useEffect(() => {

        dispatch(clearRefundViewAndCustomerState());

        let payload: { refundId: any; country: string; linerId: any } | null = null;

        if (encryptdoc && linerId && blCountry) {
            payload = {
                refundId: AESDecrypt(encryptdoc),
                country: blCountry,
                linerId: linerId,
            };
        } else if ((!encryptdoc) && recentId?.docId && linerId && blCountry) {
            payload = {
                refundId: recentId?.docId,
                country: blCountry,
                linerId: linerId,
            };
        }

        if (!payload) return;

        dispatch(getRefundDetails(payload))
            .unwrap()
            .then((res: any) => {
                const form = res?.data ?? {};
                if (form?.countryCode) setBlCountry(form.countryCode);
                // setValue('blNumber', data?.blNumber ?? '');
                // setValue('consignee', data?.consignee ?? '');
                // setValue('requestedBy', data?.requestedBy ?? '');
                setValue('countryAreaCode', form?.contactCode ?? '');
                setValue('contactNumber', form?.contactNo ?? '');
                setValue('refundType', form?.refundTp ?? '');
                setValue('refundTo', form?.refundTo ?? '');
                setValue('remarks', form?.userRemarks ?? '');
                setValue('accountHolderName', form?.accountHolderName ?? '');
                setValue('bankName', form?.bankName ?? '');
                setValue('accountNumber', form?.bankAccNo ?? '');
                setValue('swiftCode', form?.swiftCode ?? '');
                setValue('statusLabel', form?.statusLabel ?? '');
                setValue('statusId', form?.status != null ? Number(form.status) : null);
                setValue('deductionAmount', form?.deductionAmt ?? '');
                setValue('deductionCcy', form?.deductionAmtCurr?.trim() ?? '');
                setValue('refundedAmount', form?.refundAmt ?? '');
                // setValue('refundedCcy', form?.refundAmtCurr?.trim() ?? '');
                setValue('linerRemarks', form?.linerRemarks ?? '');

                if (Array.isArray(form?.attList)) {
                    if (form.attList.length > 0) {
                        dispatch(updateCustomerAttData(form.attList));
                    }

                    const nameByKey: Record<string, any> = {};
                    const filePathByKey: Record<string, any> = {};

                    for (let i = 0; i < form.attList.length; i++) {
                        const key = form.attList[i]?.attachmentId ?? form.attList[i]?.rowNumber ?? null;
                        if (key == null) continue;

                        nameByKey[key] = { name: form.attList[i]?.fileName || null };
                        filePathByKey[key] = form.attList[i]?.filePath || null;
                    }

                    dispatch(updateSelectedCustomerAttachmentName(nameByKey));
                    dispatch(updateSelectedCustomerAttachmentFilePath(filePathByKey));
                }
            })
            .catch(() => { });
    }, [
        encryptdoc,
        recentId?.docId,
        blCountry,
        linerId,

    ]);

    useEffect(() => {
        setCustomerLoadingFileState({});
        dispatch(clearCustomerFields());

        if (blCountry && linerId && !encryptdoc && !recentId && blNum) {
            dispatch(getRefundCustomerAttConf({ country: blCountry, liner: linerId }));
        }
    }, [blCountry, linerId, encryptdoc, recentId, blNum, location.pathname]);

    useEffect(() => {
        if (!blCountry || !linerId) return;
        dispatch(getRefundTypeList({ dataCountry: blCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setRefundTypeList(res ?? []))
            .catch(() => setRefundTypeList([]));

        dispatch(getRefundCountryAreaCodeList({ dataCountry: blCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setCountryAreaCodeList(res ?? []))
            .catch(() => setCountryAreaCodeList([]));

        dispatch(getRefundBankList({ dataCountry: blCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setBankNameList(res ?? []))
            .catch(() => setBankNameList([]));

        dispatch(getRefundCcyList({ dataCountry: blCountry, linerId: linerId }))
            .unwrap()
            .then((res: any) => setRefundCcyList(res ?? []))
            .catch(() => setRefundCcyList([]));

    }, [blCountry, linerId]);

    useEffect(() => {
        // Fresh request only: auto-fill Refund To with consignee name.
        if (encryptdoc || recentId?.docId) return;

        // const currentRefundTo = String(watch('refundTo') ?? '').trim();
        // if (currentRefundTo !== '') return;

        const consigneeName =
            oblPartyList?.find?.((p: any) => {
                const tp = String(p?.partyType ?? '').trim().toLowerCase();
                return tp.includes('consignee') || tp == 'cn';
            })?.partyName ?? '';

        // if (!consigneeName || String(consigneeName).trim() === '') return;

        setValue('refundTo', String(consigneeName).trim(), {
            shouldDirty: false,
            shouldValidate: true,
        });
    }, [encryptdoc, recentId?.docId, oblPartyList, setValue, watch]);

    useEffect(() => {
        const blid = AESDecrypt(blNum);
        if (!blid || !blCountry || !linerId) {
            setBlAllInvoicesRows([]);
            return;
        }
        dispatch(
            getAllInvoicesForBl({
                blid,
                country: blCountry,
                linerId: Number(linerId),
            })
        )
            .unwrap()
            .then((rows: any[]) => setBlAllInvoicesRows(rows ?? []))
            .catch(() => setBlAllInvoicesRows([]));
    }, [blNum, blCountry, linerId]);


    const isCustomerFileUploadBusy = (): boolean =>
        Object.values(customerLoadingFileState).some(
            (val) => val != 100 && val != 0 && val != -1 && val != undefined
        );

    // const isLinerFileUploadBusy = (): boolean =>
    //     Object.values(linerLoadingFileState).some(
    //         (val) => val != 100 && val != 0 && val != -1 && val != undefined
    //     );



    const isRefundMandatoryCustomerDocumentsIncomplete = (): boolean => {
        const docAttachmentKey = docAttachment;
        const viewFormAttKey = refundViewForm?.attList ?? [];

        if (docAttachmentKey?.list?.length === 0) {
            return true;
        }

        return docAttachmentKey.list.filter((x: any) => x.m).findIndex((d: any) => {
            const key = d?.attachmentId ?? d?.rowNumber ?? null;
            const serverEntry = serverFilePath?.[key];
            const resolvedServerPath = Array.isArray(serverEntry) ? serverEntry?.[0] : serverEntry;

            return (
                !selectedAttachmentName?.[key] ||
                (((customerLoadingFileState[key] !== 100 || !resolvedServerPath) &&
                    ((viewFormAttKey.findIndex(
                        (i: any) => i?.docId == d?.docId && i?.attachmentId
                    ) == -1) ||
                        customerLoadingFileState[key] == -1)))
            );
        }) != -1;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const consigneeName =
        oblPartyList?.find?.((p: any) => {
            const tp = String(p?.partyType ?? '').trim().toLowerCase();
            return tp.includes('consignee');
        })?.partyName ?? '';

    const onSubmitForm = (name: 'Submitted' | 'Draft') =>
        handleSubmit((payload: any) => {

            // const consigneeName =
            //     oblPartyList?.find?.((p: any) => {
            //         const tp = String(p?.partyType ?? '').trim().toLowerCase();
            //         return tp.includes('consignee') || tp == 'cn';
            //     })?.partyName ?? '';

            const viewFormAttKey = refundViewForm?.attList ?? [];
            const documents = (docAttachment?.list ?? [])?.filter((d: any) => {
                const key = (d?.attachmentId ?? d?.rowNumber ?? null);
                return customerLoadingFileState[key] != -1;
            })?.map((item: any) => {
                const key = (item?.attachmentId ?? item?.rowNumber ?? null);
                const serverEntry = serverFilePath?.[key];
                return {
                    refundRequestAttId:
                        refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.SUBMITTED ||
                            refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED ||
                            refundViewForm?.statusLabel == 'Draft'
                            ? viewFormAttKey?.find(
                                (x: any) => item?.docId === x?.docId
                            )?.attachmentId ?? null
                        : null,
                    docid: item?.docId ?? null,
                    fileName: selectedAttachmentName?.[key]?.name ?? '',
                    filePath: selectedAttachmentFilePath?.[key] ?? null,
                    tempFileName: Array.isArray(serverEntry) ? (serverEntry?.[1] ?? null) : null,
                    tempFilePath: Array.isArray(serverEntry) ? (serverEntry?.[0] ?? null) : null,
                };
            })?.filter((x) => x?.fileName);

            const decryptedBlId = AESDecrypt(blNum);
            const blId = decryptedBlId ? Number(decryptedBlId) : null;

            const refundRequestId =
                refundViewForm?.statusLabel != REFUND_EVENT_STATUS_LABEL.SUBMITTED &&
                    refundViewForm?.statusLabel != REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED &&
                    refundViewForm?.statusLabel != 'Draft' &&
                    !(refundViewForm?.statusLabel ?? '').includes(REFUND_EVENT_STATUS_LABEL.REJECTED)
                    ? null
                    : refundViewForm?.refundRequestId || null;
            const isDraftSubmit = name === 'Draft';
            const groupLevel = Number(refundViewForm?.groupLevel ?? 0);

            // const approvedStatusId =
            //     groupLevel === 1 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L1
            //         : groupLevel === 2 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L2
            //             : groupLevel === 3 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L3
            //                 : groupLevel === 4 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L4
            //                     : groupLevel === 5 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L5
            //                         : groupLevel === 6 ? REFUND_EVENT_STATUS_ID.APPROVED_BY_L6
            //                             : undefined;

            const finalPayload = {
                // Backend accepted payload (RefundRequestPayload)
                refundRequestId: refundRequestId,
                blId: blId ?? '',
                countryCode: blCountry ?? '',
                linerId: +selectedLinerId,
                requestedPartnerId: profileDetails?.partnerType === 'customer' ? (profileDetails?.partnerId ?? null) : null,
                // customerNm: profileDetails?.partnerName ?? '',
                customerNm: consigneeName ?? '',
                requestedBy: profileDetails?.email ?? '',
                contactCode: payload?.countryAreaCode ?? '',
                contactNo: payload?.contactNumber ?? '',
                refundTp: payload?.refundType ?? '',
                // refundTo: payload?.refundTo ?? '',
                refundTo: consigneeName ?? '',
                userRemarks: payload?.remarks ?? '',
                accountHolderName: payload?.accountHolderName ?? '',
                bankName: payload?.bankName ?? '',
                bankAccNo: payload?.accountNumber ?? '',
                swiftCode: payload?.swiftCode ?? '',
                // status:
                //     isDraftSubmit
                //         ? REFUND_EVENT_STATUS_ID.NOT_SUBMITTED
                //         : approvedStatusId ?? REFUND_EVENT_STATUS_ID.SUBMITTED,
                status:
                    isDraftSubmit
                        ? REFUND_EVENT_STATUS_ID.NOT_SUBMITTED
                        : REFUND_EVENT_STATUS_ID.SUBMITTED,
                // statusLabel:
                //     isDraftSubmit
                //         ? REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED
                //         : refundViewForm?.groupLevel
                //             ? `Approved by L${refundViewForm.groupLevel}`
                //             : REFUND_EVENT_STATUS_LABEL.SUBMITTED,
                groupLevel: refundViewForm?.groupLevel ?? 0,
                customerDocuments: documents ?? [],
            };

            dispatch(submitRefundRequest(finalPayload))
                .unwrap()
                .then((result: any) => {
                    toast.success(name === 'Draft' ? 'Saved as draft successfully' : result?.responseMsg);
                    dispatch(updateDashbaordRefresh(true));
                    setTimeout(() => {
                        dispatch(
                            getInstructionList({
                                blNum: AESDecrypt(blNum),
                                country: blCountry,
                            })
                        );
                    }, 3000);
                    onBack();
                })
                .catch((e: any) => toast.error(e?.responseMsg || 'Something went wrong'));
        });

    const isLiner = profileDetails?.partnerType === 'liner';
    const isCustomer = profileDetails?.partnerType === 'customer';

    const isSubmitted = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.SUBMITTED;
    const isCompleted = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.COMPLETED;
    // const isNotified = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.NOTIFIED;
    const isRejected = (refundViewForm?.statusLabel ?? '').includes(REFUND_EVENT_STATUS_LABEL.REJECTED);
    const isRefunded = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.REFUNDED;
    const isDrafted = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED;
    const isApprovedByL1 = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.APPROVED_BY_L1;
    const isRejectedByL1 = refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.REJECTED_BY_L1;


    const showAmountCurrencyFields =
        isLiner || (isCustomer && (isCompleted || isRefunded));

    const accessGroupLevel = [rbac.rbacDetails.refundLevelOne, rbac.rbacDetails.refundLevelTwo, rbac.rbacDetails.refundLevelThree, rbac.rbacDetails.refundLevelFour, rbac.rbacDetails.refundLevelFive, rbac.rbacDetails.refundLevelSix, rbac.rbacDetails.refundLevelSeven, rbac.rbacDetails.refundLevelEight, rbac.rbacDetails.refundLevelNine, rbac.rbacDetails.refundLevelTen];

    const refundGroupLevel = Number(refundViewForm?.groupLevel ?? 0);

    console.log('accessGroupLevel', accessGroupLevel);
    const hasRefundApproveRejectAccess = rbac?.rbacDetails?.refundApproveReject === 'Y';


    const showRefundApproveRejectButtons =
        isLiner &&
        dataCountry === blCountry &&
        hasRefundApproveRejectAccess &&
        (accessGroupLevel[refundGroupLevel] == 'Y' ||
            !accessGroupLevel.includes('Y')) &&
        !(instructionList
            ?.filter((v: any) => v?.docId === refundViewForm?.refundRequestId)
            ?.map(
                (u: any) =>
                    u?.event === 'Completed' ||
                    u?.event === REFUND_EVENT_STATUS_LABEL.REFUNDED
            )
            ?.includes(true)) &&
        refundViewForm?.statusLabel != REFUND_EVENT_STATUS_LABEL.COMPLETED &&
        refundViewForm?.statusLabel != REFUND_EVENT_STATUS_LABEL.REFUNDED &&
        !(refundViewForm?.statusLabel ?? '').includes(
            REFUND_EVENT_STATUS_LABEL.REJECTED
        );

    /** Matches DO Extension: grey out approve/reject when terminal + form is read-only. */
    const refundApproveRejectButtonsDisabled =
        refundViewForm?.statusLabel === REFUND_EVENT_STATUS_LABEL.COMPLETED ||
            refundViewForm?.statusLabel === REFUND_EVENT_STATUS_LABEL.REFUNDED ||
            (refundViewForm?.statusLabel ?? '').includes(
                REFUND_EVENT_STATUS_LABEL.REJECTED
            )
            ? disableFields
            : false;

    const disableLinerRefundAmountFields =
        profileDetails?.partnerType != 'liner' ||
        !hasRefundApproveRejectAccess ||
        !(accessGroupLevel[refundGroupLevel] == 'Y' || !accessGroupLevel.includes('Y')) ||
        isCompleted ||
        isRejected ||
        isRefunded ||
        instructionList
            ?.filter((v: any) => v?.docId === refundViewForm?.refundRequestId)
            ?.map((u: any) => u?.event === 'Completed' || u?.event === 'Refunded')
            ?.includes(true);

    const handleReset = () => {
        setValue('countryAreaCode', '');
        setValue('contactNumber', '');
        setValue('refundType', '');
        // setValue('refundTo', '');
        setValue('remarks', '');
        setValue('accountHolderName', '');
        setValue('bankName', '');
        setValue('accountNumber', '');
        setValue('swiftCode', '');

        setCustomerLoadingFileState({});
        dispatch(updateSelectedCustomerAttachmentName({}));
        dispatch(updateSelectedCustomerAttachmentFilePath({}));
        dispatch(updateSelectedCustomerFileServerPath({}));
        clearErrors();
    };

    console.log(Number(watch('refundedAmount')), "Number(watch('refundedAmount'))")

   
    const handleApprove = async () => {
        setValue('actionType', 'A');
        clearErrors(['linerRemarks']);
        const isValid = await trigger(['deductionCcy', 'deductionAmount', 'refundedAmount']);
        if (!isValid) return;

        const payload = {
            refundRequestId: refundViewForm?.refundRequestId ?? (encryptdoc ? AESDecrypt(encryptdoc) : null),
            linerId: Number(selectedLinerId),
            groupLevel: !accessGroupLevel.includes('Y') ? null : (refundViewForm?.groupLevel ?? 0) + 1,
            nextLevel: null,
            refundAmt: watch('refundedAmount'),
            refundAmtCurr: null,
            deductionAmt: watch('deductionAmount'),
            deductionAmtCurr: watch('deductionCcy') ?? '',
            status: 'A',
            linerRemarks: watch('linerRemarks') ?? '',
        };

        dispatch(approveRejectRefund(payload))
            .unwrap()
            .then((result: any) => {
                toast.success(result?.responseMsg);
                dispatch(updateDashbaordRefresh(true));
                setTimeout(() => {
                    dispatch(
                        getInstructionList({
                            blNum: AESDecrypt(blNum),
                            country: blCountry,
                        })
                    );
                }, 3000);
                onBack();
            })
            .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    };

    // const handleMarkAsRefunded = async () => {
    //     setValue('actionType', 'M');
    //     clearErrors(['deductionCcy', 'deductionAmount', 'refundedAmount', 'linerRemarks']);

    //     const payload = {
    //         refundRequestId: refundViewForm?.refundRequestId ?? (encryptdoc ? AESDecrypt(encryptdoc) : null),
    //         linerId: Number(selectedLinerId),
    //         groupLevel: !accessGroupLevel.includes('Y') ? null : (refundViewForm?.groupLevel ?? 0),
    //         nextLevel: null,
    //         refundAmt: watch('refundedAmount'),
    //         refundAmtCurr: null,
    //         deductionAmt: watch('deductionAmount'),
    //         deductionAmtCurr: watch('deductionCcy') ?? '',
    //         status: 'RF',
    //         linerRemarks: watch('linerRemarks') ?? '',
    //     };

    //     dispatch(approveRejectRefund(payload))
    //         .unwrap()
    //         .then((result: any) => {
    //             toast.success(result?.responseMsg);
    //             dispatch(updateDashbaordRefresh(true));
    //             setTimeout(() => {
    //                 dispatch(
    //                     getInstructionList({
    //                         blNum: AESDecrypt(blNum),
    //                         country: blCountry,
    //                     })
    //                 );
    //             }, 3000);
    //             navigate(-1);
    //         })
    //         .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    // };

    const handleReject = async () => {
        setValue('actionType', 'R');
        setValue('deductionCcy', '');
        setValue('deductionAmount', '');
        // setValue('refundedCcy', '');
        setValue('refundedAmount', '');
        clearErrors(['deductionCcy', 'deductionAmount', 'refundedAmount']);
        const isValid = await trigger(['linerRemarks']);
        if (!isValid) return;

        const payload = {
            refundRequestId: refundViewForm?.refundRequestId ?? (encryptdoc ? AESDecrypt(encryptdoc) : null),
            linerId: Number(selectedLinerId),
            groupLevel: !accessGroupLevel.includes('Y') ? null : (refundViewForm?.groupLevel ?? 0) + 1,
            nextLevel: null,
            refundAmt: null,
            refundAmtCurr: null,
            deductionAmt: null,
            deductionAmtCurr: watch('deductionCcy') ?? '',
            status: 'R',
            linerRemarks: watch('linerRemarks') ?? '',
        };

        dispatch(approveRejectRefund(payload))
            .unwrap()
            .then((result: any) => {
                toast.success(result?.responseMsg);
                dispatch(updateDashbaordRefresh(true));
                setTimeout(() => {
                    dispatch(
                        getInstructionList({
                            blNum: AESDecrypt(blNum),
                            country: blCountry,
                        })
                    );
                }, 3000);
                onBack();
            })
            .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    };

    // const handleNotify = async () => {
    //     setValue('actionType', 'N');
    //     setValue('deductionCcy', '');
    //     setValue('deductionAmount', '');
    //     setValue('refundedCcy', '');
    //     setValue('refundedAmount', '');
    //     clearErrors(['deductionCcy', 'deductionAmount', 'refundedCcy', 'refundedAmount']);

    //     const isValid = await trigger(['linerRemarks']);
    //     if (!isValid) return;

    //     const payload = {
    //         refundId: encryptdoc ? AESDecrypt(encryptdoc) : null,
    //         status: REFUND_EVENT_STATUS_ID.NOTIFIED,
    //         statusLabel: REFUND_EVENT_STATUS_LABEL.NOTIFIED,
    //         deduction: '',
    //         deductionCcy: '',
    //         refundedAmount: '',
    //         refundedCcy: '',
    //         linerRemarks: watch('linerRemarks'),
    //         country: blCountry,
    //         linerId: Number(selectedLinerId),
    //     };

    //     dispatch(approveRejectRefund(payload))
    //         .unwrap()
    //         .then((result: any) => {
    //             toast.success(result?.responseMsg);
    //             dispatch(updateDashbaordRefresh(true));
    //             setTimeout(() => {
    //                 dispatch(
    //                     getInstructionList({
    //                         blNum: AESDecrypt(blNum),
    //                         country: blCountry,
    //                     })
    //                 );
    //             }, 3000);
    //             navigate(-1);
    //         })
    //         .catch((err: any) => toast.error(err?.responseMsg || 'Something went wrong'));
    // };


    /* Section labels (temporarily disabled)
    const SectionLabel = ({ label }: { label: string }) => (
        <div className='col-lg-12 mb-2 mt-1'>
            <div className='d-flex align-items-center'>
                <span
                    className='text-uppercase fw-bold me-2'
                    style={{ fontSize: '13px', color: '#475569', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}
                >
                    {label}
                </span>
                <hr className='flex-grow-1 my-0' style={{ borderColor: '#D1D5DB', borderWidth: '1.5px', opacity: 1 }} />
            </div>
        </div>
    );
    */


    return (
        <>
            <Card className='h-100 overflow-auto'>
                <Card.Header
                    className='d-flex align-items-center border-bottom sticky-top bg-white'
                >
                    <FontAwesomeIcon
                        icon={faArrowAltCircleLeft}
                        className='text-warning cursor-pointer fa-lg me-1 mb-2'
                        onClick={onBack}
                    />
                    <div className='d-flex align-items-center flex-wrap gap-2 mb-1'>
                        <div className='d-flex align-items-center gap-3 mb-1 flex-wrap'>
                            <h2 className='fs-0 text-primary mb-0'>Refund Request</h2>
                            {displayNumber ? (
                                <div className='d-flex align-items-center'>
                                    <span className='text-muted me-2'>BL No.</span>
                                    <span className='fw-bold me-2'>{displayNumber}</span>
                                    <button
                                        type='button'
                                        className='btn btn-link p-0'
                                        onClick={handleCopyBL}
                                        title='Copy BL number'
                                    >
                                        <FontAwesomeIcon icon={faCopy} className='text-primary' />
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </Card.Header>

                <Card.Body>
                    <Form onSubmit={onSubmitForm('Submitted')} autoComplete='off'>
                        <div className='row'>
                            {/* {blAllInvoicesRows.length > 0 && ( */}
                            <div className='col-lg-12 mb-3'>
                                <DataTable
                                    data={blAllInvoicesRows}
                                    gridName='RefundInvoiceList'
                                    columns={refundInvoiceList}
                                    sortable={false}
                                    searchable={false}
                                    showColumnFilter={false}
                                    selection={false}
                                    perPage={10}
                                    pagination={false}
                                    showExport={false}
                                    uniqueFilterKey='refundInvoiceList'
                                    autoHeight={true}
                                    staticHiddenCols={[]}
                                    maxHeight='250px'
                                    tableMinHeight={0}
                                />
                            </div>
                            {/* )} */}

                            {/* ── Security Deposit Details ── */}
                            {/* <SectionLabel label='Security deposit details' /> */}

                            {/* ── Refund Details ── */}
                            {/* <SectionLabel label='Refund details' /> */}

                            {/* BL Number / Consignee / Requested By — hidden for now; BL for submit comes from OBL (`oblNumber`) */}
                            {/*
                            <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1'>BL Number</Form.Label>
                                <Form.Control
                                    className='fs--1'
                                    {...register('blNumber')}
                                    disabled={true}
                                />
                            </div>

                            <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1'>Consignee</Form.Label>
                                <Form.Control
                                    className='fs--1'
                                    {...register('consignee')}
                                    disabled={true}
                                />
                            </div>

                            <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1'>Requested By</Form.Label>
                                <Form.Control
                                    className='fs--1'
                                    {...register('requestedBy')}
                                    disabled={true}
                                />
                            </div>
                            */}

                            <div className='col-lg-4 mb-2'>
                                <Form.Group controlId='refund.contactNumber'>
                                    <Form.Label className='fs--1'>
                                        <span className='mandatory'>*</span>
                                        Contact Number
                                    </Form.Label>
                                    <div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: '100%',
                                                minWidth: 0,
                                            }}
                                        >
                                            <Form.Select
                                                className={`fs--1 form-control ${errors.countryAreaCode ? 'is-invalid' : ''
                                                    }`}
                                                style={{ width: '100px', flexShrink: 0 }}
                                                disabled={disableFields}
                                              {...register('countryAreaCode')}
                                                aria-label='Area code'
                                            >
                                                <option value=''>Select</option>
                                                {countryAreaCodeList?.map((item: any) => (
                                                    <option key={item.value} value={item.value}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            <Form.Control
                                                type='tel'
                                                maxLength={20}
                                                disabled={disableFields}
                                                aria-label='Contact number'
                                                {...register('contactNumber')}
                                                className={`fs--1 form-control ${errors.contactNumber ? 'is-invalid' : ''
                                                    }`}
                                                style={{
                                                    borderTopLeftRadius: 0,
                                                    borderBottomLeftRadius: 0,
                                                    flex: '1 1 auto',
                                                    minWidth: 0,
                                                }}
                                                onInput={(e: any) =>
                                                    setValue(
                                                        'contactNumber',
                                                        e.target.value.replace(/[^0-9+]/g, '')
                                                    )
                                                }
                                                onBlur={(e) => setValue('contactNumber', e.target.value.trim())}
                                            />
                                        </div>
                                        {errors.contactNumber && (
                                            <div className='invalid-feedback' style={{ display: 'block' }}>
                                                {errors.contactNumber?.message?.toString()}
                                            </div>
                                        )}
                                    </div>
                                </Form.Group>
                            </div>

                            <div className='col-lg-4 mb-2'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'>Refund Type</Form.Label>
                                <Form.Select
                                    value={watch('refundType')}
                                    {...register('refundType')}
                                    disabled={disableFields}
                                    className={`fs--1 ${errors.refundType ? 'is-invalid' : ''}`}
                                >
                                    <option value=''>Select Type</option>
                                    {refundTypeList?.map((item: any) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div className='invalid-feedback d-block'>
                                    {errors.refundType?.message?.toString()}
                                </div>
                            </div>

                            {/* <div className='col-lg-4 mb-3'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'>Refund To</Form.Label>
                                <Form.Select
                                    value={watch('refundTo')}
                                    {...register('refundTo')}
                                    disabled={disableFields}
                                    className={`fs--1 ${errors.refundTo ? 'is-invalid' : ''}`}
                                >
                                    <option value=''>Select</option>
                                    {refundToList?.map((item: any) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div className='invalid-feedback d-block'>
                                    {errors.refundTo?.message?.toString()}
                                </div>
                            </div> */}



                            <div className='col-lg-4 mb-2'>
                                {/* <span className='mandatory'>*</span> */}
                                <Form.Label className='fs--1'>Refund To</Form.Label>
                                <Form.Control
                                    {...register('refundTo')}
                                    disabled={true}
                                    maxLength={100}
                                    className={`fs--1 ${errors.refundTo ? 'is-invalid' : ''}`}
                                    onBlur={(e) => setValue('refundTo', e.target.value.trim())}
                                />
                                <div className='invalid-feedback d-block'>
                                    {errors.refundTo?.message?.toString()}
                                </div>
                            </div>


                            {/* ── Bank Details ── */}
                            {/* <SectionLabel label='Bank details' /> */}

                            <div className='col-lg-4 mb-3'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'>Account Holder Name</Form.Label>
                                <Form.Control
                                    className={`fs--1 ${errors.accountHolderName ? 'is-invalid' : ''}`}
                                    {...register('accountHolderName')}
                                    disabled={disableFields}
                                    maxLength={50}
                                    onBlur={(e) => setValue('accountHolderName', e.target.value.trim())}
                                />
                                <div className='invalid-feedback d-block'>
                                    {errors.accountHolderName?.message?.toString()}
                                </div>
                            </div>

                            {/* <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1'>Bank Name</Form.Label>
                                <Form.Select
                                    value={watch('bankName')}
                                    {...register('bankName')}
                                    disabled={disableFields}
                                    className='fs--1'
                                >
                                    <option value=''>Select</option>
                                    {bankNameList?.map((item: any) => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div> */}


                            <div className='col-lg-4 mb-3'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'> Bank Name </Form.Label>
                                <Form.Control
                                    className={`fs--1 ${errors.bankName ? 'is-invalid' : ''}`}
                                    {...register('bankName')}
                                    disabled={disableFields}
                                    maxLength={100}
                                    onBlur={(e) => setValue('bankName', e.target.value.trim())}
                                />
                                <div className='invalid-feedback d-block'>
                                    {errors.bankName?.message?.toString()}
                                </div>
                            </div>



                            <div className='col-lg-4 mb-3'>
                                <span className='mandatory'>*</span>
                                <Form.Label className='fs--1'>Account Number</Form.Label>
                                <Form.Control
                                    className={`fs--1 ${errors.accountNumber ? 'is-invalid' : ''}`}
                                    {...register('accountNumber')}
                                    disabled={disableFields}
                                    maxLength={30}
                                    onBlur={(e) => setValue('accountNumber', e.target.value.trim())}
                                />
                                <div className='invalid-feedback d-block'>
                                    {errors.accountNumber?.message?.toString()}
                                </div>
                            </div>

                            <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1'>SWIFT Code</Form.Label>
                                <Form.Control
                                    className={`fs--1 ${errors.swiftCode ? 'is-invalid' : ''}`}
                                    {...register('swiftCode')}
                                    disabled={disableFields}
                                    maxLength={11}
                                    onBlur={(e) => setValue('swiftCode', e.target.value.trim())}
                                />
                                {/* <div className='invalid-feedback d-block'>
                                    {errors.swiftCode?.message?.toString()}
                                </div> */}
                            </div>

                            <div className='col-lg-4 mb-3'>
                                <Form.Label className='fs--1 fw-medium'>Remarks</Form.Label>
                                <Form.Control
                                    as='textarea'
                                    disabled={disableFields}
                                    className='fs--1'
                                    rows={2}
                                    maxLength={100}
                                    {...register('remarks')}
                                    onBlur={(e) => setValue('remarks', e.target.value.trim())}
                                />

                            </div>

                        </div>

                        <div className='mb-4'>
                            {isCustomer && !disableFields && (
                                <span className='badge bg-secondary mb-2'>
                                    {t('resource_message:docInfo')}{' '}
                                </span>
                            )}
                            <RefundCustomerDocuments
                                isViewMode={disableFields}
                                customerLoadingFileState={customerLoadingFileState}
                                setCustomerLoadingFileState={setCustomerLoadingFileState}
                            />
                        </div>

                        {/*
                        {isLiner && isCompleted && (
                            <RefundLinerDocuments
                                isViewMode={false}
                                linerLoadingFileState={linerLoadingFileState}
                                setLinerLoadingFileState={setLinerLoadingFileState}
                            />
                        )}
                        */}

                        {/* {isLiner && (
                            <div className='col-lg-12 mt-1 mb-4'>
                                <div className='d-flex align-items-center'>
                                    <span
                                        className='text-uppercase fw-bold me-2'
                                        style={{
                                            fontSize: '13px',
                                            color: '#9ca3af',
                                            letterSpacing: '0.07em',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        Liner Review
                                    </span>
                                    <hr
                                        className='flex-grow-1 my-0'
                                        style={{ borderColor: '#e2e8f0', borderWidth: '1.5px', opacity: 1 }}
                                    />
                                </div>
                            </div>
                        )} */}

                        {/* ── Refund Summary (customer label only in post-submit / non-draft view) ── */}
                        <div className='col-lg-12 mt-2'>
                            {/* {isCustomer &&
                                (isCompleted || isRejected || isRefunded) && (
                                    <div className='col-lg-12 mt-1 mb-4'>
                                        <div className='d-flex align-items-center'>
                                            <span
                                                className='text-uppercase fw-bold me-2'
                                                style={{
                                                    fontSize: '13px',
                                                    color: '#9ca3af',
                                                    letterSpacing: '0.07em',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                Refund Summary
                                            </span>
                                            <hr
                                                className='flex-grow-1 my-0'
                                                style={{ borderColor: '#e2e8f0', borderWidth: '1.5px', opacity: 1 }}
                                            />
                                        </div>
                                    </div>
                                )} */}

                            {showAmountCurrencyFields && (
                                <div className='row'>
                                    <div className='col-lg-3 col-md-6 mb-3'>
                                        <Form.Label className='fs--1 fw-medium'>Currency</Form.Label>
                                        <Form.Select
                                            value={watch('deductionCcy')}
                                            {...register('deductionCcy', {
                                                onChange: () => {
                                                    clearErrors(['deductionCcy']);
                                                },
                                            })}
                                            disabled={
                                                isCustomer || disableLinerRefundAmountFields
                                            }
                                            className={`fs--1 ${errors.deductionCcy ? 'is-invalid' : ''}`}
                                        >
                                            {/* <option value=''>Select</option>
                                            <option value='USD'>USD</option>
                                            <option value='INR'>INR</option>
                                            <option value='ZAR'>ZAR</option> */}

                                            <option value=''>Select Type</option>
                                            {refundCcyList?.map((item: any) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}

                                        </Form.Select>
                                        <div className='invalid-feedback d-block'>
                                            {errors.deductionCcy?.message?.toString()}
                                        </div>
                                    </div>

                                    <div className='col-lg-3 col-md-6 mb-3'>
                                        <Form.Label className='fs--1 fw-medium'>Deduction Amount</Form.Label>
                                        <Form.Control
                                            className={`fs--1 ${errors.deductionAmount ? 'is-invalid' : ''}`}
                                            {...register('deductionAmount')}
                                            disabled={
                                                isCustomer || disableLinerRefundAmountFields
                                            }
                                            maxLength={19}
                                            inputMode='decimal'
                                            onInput={(e: React.FormEvent<HTMLInputElement>) =>
                                                setValue(
                                                    'deductionAmount',
                                                    sanitizeAmountInput((e.target as HTMLInputElement).value),
                                                    { shouldValidate: true, shouldDirty: true }
                                                )
                                            }
                                            onBlur={(e) => setValue('deductionAmount', e.target.value.trim())}
                                        />
                                        <div className='invalid-feedback d-block'>
                                            {errors.deductionAmount?.message?.toString()}
                                        </div>
                                    </div>

                                    {/* <div className='col-lg-3 col-md-6 mb-3'>
                                        <Form.Label className='fs--1 fw-medium'>Refund Currency</Form.Label>
                                        <Form.Select
                                            value={watch('refundedCcy')}
                                            {...register('refundedCcy', {
                                                onChange: () => {
                                                    clearErrors(['refundedCcy']);
                                                },
                                            })}
                                            disabled={
                                                isCustomer || disableLinerRefundAmountFields
                                            }
                                            className={`fs--1 ${errors.refundedCcy ? 'is-invalid' : ''}`}
                                        >
                                         
                                            <option value=''>Select Type</option>
                                            {refundCcyList?.map((item: any) => (
                                                <option key={item.value} value={item.value}>{item.label}</option>
                                            ))}

                                        </Form.Select>
                                        <div className='invalid-feedback d-block'>
                                            {errors.refundedCcy?.message?.toString()}
                                        </div>
                                    </div> */}

                                    <div className='col-lg-3 col-md-6 mb-3'>
                                        <Form.Label className='fs--1 fw-medium'>Refund Amount</Form.Label>
                                        <Form.Control
                                            className={`fs--1 ${errors.refundedAmount ? 'is-invalid' : ''}`}
                                            {...register('refundedAmount')}
                                            disabled={
                                                isCustomer || disableLinerRefundAmountFields
                                            }
                                            maxLength={19}
                                            inputMode='decimal'
                                            onInput={(e: React.FormEvent<HTMLInputElement>) =>
                                                setValue(
                                                    'refundedAmount',
                                                    sanitizeAmountInput((e.target as HTMLInputElement).value),
                                                    { shouldValidate: true, shouldDirty: true }
                                                )
                                            }
                                            onBlur={(e) => setValue('refundedAmount', e.target.value.trim())}
                                        />
                                        <div className='invalid-feedback d-block'>
                                            {errors.refundedAmount?.message?.toString()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {(hasRefundApproveRejectAccess || disableFields) && (
                                <div className='row'>
                                    <div className='col-lg-4 mb-3'>
                                        <Form.Label className='fs--1 fw-medium'>Liner Remarks</Form.Label>
                                        <Form.Control
                                            as='textarea'
                                            disabled={
                                                isCustomer || disableLinerRefundAmountFields
                                            }
                                            className={`fs--1${errors.linerRemarks ? ' is-invalid' : ''}`}
                                            rows={2}
                                            maxLength={100}
                                            {...register('linerRemarks', {
                                                onChange: () => {
                                                    clearErrors(['linerRemarks']);
                                                },
                                            })}
                                            onBlur={(e) =>
                                                setValue('linerRemarks', e.target.value.trim(), {
                                                    shouldValidate: true,
                                                    shouldDirty: true,
                                                })
                                            }
                                        />
                                        <div className='invalid-feedback d-block'>{errors.linerRemarks?.message?.toString()}</div>
                                    </div>
                                </div>
                            )}

                            {(refundViewForm?.statusLabel == REFUND_EVENT_STATUS_LABEL.SUBMITTED ||
                                refundViewForm?.statusLabel?.includes(
                                    REFUND_EVENT_STATUS_LABEL.REJECTED
                                ) ||
                                refundViewForm?.statusLabel == 'Draft' ||
                                refundViewForm?.statusLabel ==
                                REFUND_EVENT_STATUS_LABEL.NOT_SUBMITTED) &&
                                profileDetails?.partnerType != 'liner' &&
                                disableFields &&
                                refundViewForm?.requestedPartnerId ==
                                profileDetails?.partnerId && (
                                    <div className='d-flex justify-content-center align-items-center mb-3'>
                                        <Button
                                            variant='falcon-primary'
                                            size='sm'
                                            className='me-2'
                                            onClick={() => {
                                                setDisableFields(false);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} className='me-2' />
                                            {t('resource_1:edit')}
                                        </Button>
                                    </div>
                                )}

                            <div className='d-flex justify-content-center align-items-center gap-3 mt-2 flex-wrap'>
                                {showRefundApproveRejectButtons && (
                                    <Button
                                        variant='success'
                                        size='sm'
                                        className='px-4'
                                        disabled={refundApproveRejectButtonsDisabled}
                                        onClick={handleApprove}
                                    >
                                        <FontAwesomeIcon icon={faCheck} className='me-2' /> Approve
                                    </Button>
                                )}
                                {showRefundApproveRejectButtons && (
                                    <Button
                                        variant='danger'
                                        size='sm'
                                        className='px-4 reject'
                                        disabled={refundApproveRejectButtonsDisabled}
                                        onClick={handleReject}
                                    >
                                        <FontAwesomeIcon icon={faTimes} className='me-2 reject' /> Reject
                                    </Button>
                                )}

                                {/* {isLiner && (isSubmitted || isNotified) && canActAtCurrentLevel && (
                                    <Button variant='warning' size='sm' className='px-4 notify' onClick={handleNotify}>
                                        <FontAwesomeIcon icon={faBell} className='me-2 notify' /> Notify
                                    </Button>
                                )} */}

                                {isLiner &&
                                    isCompleted &&
                                    rbac?.rbacDetails?.showMarkasRefundedButton === 'Y' &&
                                    !instructionList?.some(
                                        (u: any) =>
                                            u?.docId === refundViewForm?.refundRequestId &&
                                            u?.event === REFUND_EVENT_STATUS_LABEL.REFUNDED
                                    ) && (
                                        <Button
                                            variant='falcon-primary'
                                            size='sm'
                                            onClick={handleApprove}
                                            style={{ position: 'relative' }}
                                        >
                                            Mark as Refunded
                                        </Button>
                                    )}



                                {isCustomer &&
                                    !disableFields &&
                                    dataCountry === blCountry && (
                                        <Button
                                            variant='falcon-primary'
                                            size='sm'
                                            onClick={onSubmitForm('Submitted')}
                                            disabled={
                                                isCustomerFileUploadBusy() ||
                                                isRefundMandatoryCustomerDocumentsIncomplete()
                                            }
                                            style={{ position: 'relative' }}
                                        >
                                            <FontAwesomeIcon icon={faSave} className='mx-1 me-2 mt-1 text-warning' />
                                            Submit
                                        </Button>
                                    )}


                                {(isCustomer && !disableFields) && (
                                    <Button variant='falcon-primary' size='sm' onClick={handleReset} className='me-4'>
                                        <FontAwesomeIcon icon={faRedo} className='me-2' />
                                        Reset
                                    </Button>
                                )}



                                {isCustomer &&
                                    !disableFields &&
                                    rbac?.rbacDetails?.refundSaveAsDraft === 'Y' &&
                                    refundRecentEventAllowsSaveAsDraft &&
                                    refundViewForm?.statusLabel !=
                                    REFUND_EVENT_STATUS_LABEL.SUBMITTED &&
                                    !(refundViewForm?.statusLabel ?? '').includes(
                                        REFUND_EVENT_STATUS_LABEL.REJECTED
                                    ) && (
                                        <Button
                                            variant='falcon-default'
                                            size='sm'
                                            className='me-3'
                                            onClick={onSubmitForm('Draft')}
                                            disabled={isCustomerFileUploadBusy()}
                                        >
                                            <FontAwesomeIcon icon={faFilePen} className='mx-1 me-2 mt-1' />
                                            Save as Draft
                                        </Button>
                                    )}





                            </div>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </>
    );
};

export default Refund;
